import React from 'react';
import { FaStar, FaRegStar } from 'react-icons/fa';

/** Note entre 0 et 5 (inclus). */
export function clampRating0to5(r) {
  const n = Number(r);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.min(5, n);
}

/**
 * Moyenne des notes sur les avis (champ rating ou score).
 * Retourne null si aucun avis exploitable.
 */
export function averageRatingFromReviews(reviews) {
  if (!Array.isArray(reviews) || reviews.length === 0) return null;
  let sum = 0;
  let count = 0;
  for (const r of reviews) {
    const v = r?.rating ?? r?.score;
    if (v == null || v === '') continue;
    sum += clampRating0to5(v);
    count += 1;
  }
  if (count === 0) return null;
  return sum / count;
}

/**
 * Extrait la note affichable depuis un objet métier.
 * Priorité: average_rating -> avg_rating -> rating.
 */
export function getDisplayRating(source, fallback = 0) {
  if (!source || typeof source !== 'object') return clampRating0to5(fallback);
  const raw = source.average_rating ?? source.avg_rating ?? source.rating;
  const n = Number(raw);
  if (Number.isNaN(n)) return clampRating0to5(fallback);
  return clampRating0to5(n);
}

const SIZE_CLASS = {
  sm: 'w-3 h-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

/**
 * Affichage 5 étoiles aligné sur une note 0–5 (demi-étoile si partie décimale ≥ 0,5).
 */
export function RatingStarsFa({ rating, size = 'md', className = '' }) {
  const r = clampRating0to5(rating);
  const sc = SIZE_CLASS[size] || SIZE_CLASS.md;
  const fullStars = Math.min(5, Math.floor(r));
  const dec = r - fullStars;
  const hasHalfStar = dec >= 0.5 && fullStars < 5;
  const emptyCount = Math.max(0, 5 - fullStars - (hasHalfStar ? 1 : 0));

  const stars = [];
  for (let i = 0; i < fullStars; i += 1) {
    stars.push(
      <FaStar key={`f-${i}`} className={`${sc} text-yellow-400 ${className}`} aria-hidden />
    );
  }
  if (hasHalfStar) {
    stars.push(
      <div key="half" className="relative inline-flex" aria-hidden>
        <FaRegStar className={`${sc} text-yellow-400`} />
        <div className="absolute inset-0 overflow-hidden w-1/2 pointer-events-none">
          <FaStar className={`${sc} text-yellow-400`} />
        </div>
      </div>
    );
  }
  for (let i = 0; i < emptyCount; i += 1) {
    stars.push(
      <FaRegStar key={`e-${i}`} className={`${sc} text-gray-300 ${className}`} aria-hidden />
    );
  }

  return (
    <span className="inline-flex items-center gap-0.5" role="img" aria-label={`Note ${r.toFixed(1)} sur 5`}>
      {stars}
    </span>
  );
}
